import React, { useState, useEffect, useCallback, useRef } from "react";
import OutputsSummaryTable from "./OutputsSummaryTable";
import { 
    FormControl,
    FormControlLabel,
    MenuItem,
    Select,
    Skeleton,
    Grid,
    Paper,
    InputLabel,
    Box,
    Tabs,
    Tab,
    Switch,
    CircularProgress
} from "@mui/material";
import ForecastDropdown from "./ForecastDropdown";
import ForecastPeriodDropdown from "./ForecastPeriodDropdown";
import ProductFiltersButton from "./ProductFiltersButton";
import ProductFilters from "./ProductFilters";
import axiosInstance from "../AxiosInstance";

const ForecastOutputsSummary = () => {
    const [forecasts, setForecasts] = useState([]);
    const [view, setView] = useState("units");
    const [aggregationPeriod, setAggregationPeriod] = useState("monthly");
    const [selectedForecastId, setSelectedForecastId] = useState(null);
    const [selectedForecastPeriodId, setSelectedForecastPeriodId] = useState(null);
    const [selectedProductIds, setSelectedProductIds] = useState([]);
    const [selectedBrands, setSelectedBrands] = useState([]);
    const [selectedProductGroups, setSelectedProductGroups] = useState([]);
    const [summaryData, setSummaryData] = useState({ channels: [] });
    const [loadingForecasts, setLoadingForecasts] = useState(true);
    const [loadingTable, setLoadingTable] = useState(false);
    const [filtersExpanded, setFiltersExpanded] = useState(false);
    const [showActuals, setShowActuals] = useState(false);
    const [loadingActuals, setLoadingActuals] = useState(false);
    const [showCompare, setShowCompare] = useState(false);
    const previousShowActualsRef = useRef(showActuals);
    const viewSupportsActuals = view === "units" || view === "grossRev" || view === "triggers" || view === "cogs" || view === "logs";

    const getToken = () => localStorage.getItem("token");

    const handleFiltersToggle = () => {
        setFiltersExpanded(prev => !prev);
    };

    useEffect(() => {
        const storedForecast = JSON.parse(localStorage.getItem("selectedForecast"));
        const storedForecastPeriodId = localStorage.getItem("selectedForecastPeriodId");

        if (storedForecast?.forecastId) {
            setSelectedForecastId(storedForecast.forecastId);
        }

        if (storedForecastPeriodId && storedForecastPeriodId !== "null") {
            setSelectedForecastPeriodId(parseInt(storedForecastPeriodId, 10));
        } else {
            setSelectedForecastPeriodId(null);
        }

        fetchForecastsAndPeriods();
    }, []);

    const fetchForecastsAndPeriods = async () => {
        try {
            setLoadingForecasts(true);
            const token = getToken();
            const response = await axiosInstance.get("/forecasts", {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            setForecasts(response.data);
        } catch (error) {
            console.log(error);
        } finally {
            setLoadingForecasts(false);
        }
    };

    const fetchSummaryData = useCallback(async (
        { includeActuals, showTableLoader, showActualsLoader } = { includeActuals: false, showTableLoader: true, showActualsLoader: false }
    ) => {
        if (!selectedForecastId || selectedForecastPeriodId === null) {
            if (showTableLoader) setLoadingTable(false);
            if (showActualsLoader) setLoadingActuals(false);
            return;
        }

        if (showTableLoader) setLoadingTable(true);
        if (showActualsLoader) setLoadingActuals(true);

        try {
            const params = {
                forecastPeriodId: selectedForecastPeriodId,
                aggregationPeriod
            };
            if (selectedProductIds && Array.isArray(selectedProductIds) && selectedProductIds.length > 0) {
                params.productIds = selectedProductIds.join(",");
            }
            if (selectedBrands && Array.isArray(selectedBrands) && selectedBrands.length > 0) {
                params.brands = selectedBrands.join(",");
            }
            if (selectedProductGroups && Array.isArray(selectedProductGroups) && selectedProductGroups.length > 0) {
                params.productGroups = selectedProductGroups.join(",");
            }
            if (includeActuals) {
                params.includeActuals = true;
            }

            const response = await axiosInstance.get(`/forecasts/${selectedForecastId}/outputs-summary`, {
                params
            });
            setSummaryData(response.data);
        } catch (error) {
            console.error("Failed to fetch account summary:", error);
            setSummaryData({ channels: [] });
        } finally {
            if (showTableLoader) setLoadingTable(false);
            if (showActualsLoader || includeActuals) {
                setLoadingActuals(false);
            }
        }
    }, [
        selectedForecastId,
        selectedForecastPeriodId,
        selectedProductIds,
        selectedBrands,
        selectedProductGroups,
        aggregationPeriod
    ]);

    useEffect(() => {
        const includeActuals = showActuals && viewSupportsActuals;
        const toggledActuals = previousShowActualsRef.current !== includeActuals;
        previousShowActualsRef.current = includeActuals;

        if (toggledActuals && !includeActuals) {
            setLoadingActuals(false);
        }

        fetchSummaryData({
            includeActuals,
            showTableLoader: !toggledActuals,
            showActualsLoader: toggledActuals && includeActuals
        });
    }, [fetchSummaryData, showActuals, viewSupportsActuals]);

    useEffect(() => {
        if (!viewSupportsActuals && showActuals) {
            setShowActuals(false);
        }
    }, [viewSupportsActuals, showActuals]);

    const handleForecastSelected = (id, period) => {
        localStorage.setItem("selectedForecast", JSON.stringify({ forecastId: id, startPeriod: period }));
        setSelectedForecastId(id);

        const storedForecastPeriodId = localStorage.getItem("selectedForecastPeriodId");

        if (storedForecastPeriodId) {
            const forecast = forecasts.find((f) => f.forecastId === id);
            const validPeriod = forecast?.forecastPeriods.some(
                (fp) => fp.forecastPeriodId === parseInt(storedForecastPeriodId, 10)
            );
            if (validPeriod) {
                setSelectedForecastPeriodId(storedForecastPeriodId);
            } else {
                setSelectedForecastPeriodId(null);
            }
        } else {
            setSelectedForecastPeriodId(null);
        }
    };

    const handleForecastPeriodSelected = (forecastPeriodId) => {
        localStorage.setItem("selectedForecastPeriodId", forecastPeriodId);
        setSelectedForecastPeriodId(forecastPeriodId);
    };

    const handleChangeView = (event) => {
        const newView = event.target.value;
        setView(newView);
        const supportsActuals = newView === "units" || newView === "grossRev" || newView === "triggers" || newView === "cogs" || newView === "logs";
        if (!supportsActuals) {
            setShowActuals(false);
        }
    };

    const handleAggregationPeriodChange = (event, newValue) => {
        setAggregationPeriod(newValue);
    };

    const handleActualsToggle = (event) => {
        const checked = event.target.checked;
        if (checked) {
            setLoadingActuals(true);
        } else {
            setLoadingActuals(false);
            setShowCompare(false);
        }
        setShowActuals(checked);
    };

    const handleCompareToggle = (event) => {
        setShowCompare(event.target.checked);
    };

    const selectedForecast = forecasts.find((f) => f.forecastId === selectedForecastId);

    return (
        <Box className="component-container">
            <Paper elevation={2} sx={{ p: 3, mb: 2 }}>
                <Grid container spacing={3} alignItems="center">
                    <Grid item xs={12} sm="auto">
                        {loadingForecasts ? (
                            <Skeleton variant="rectangular" width={200} height={56} />
                        ) : (
                            <ForecastDropdown
                                forecasts={forecasts}
                                selectedForecastId={selectedForecastId}
                                onForecastSelected={handleForecastSelected}
                                width={200}
                            />
                        )}
                    </Grid>
                    <Grid item xs={12} sm="auto">
                        {loadingForecasts ? (
                            <Skeleton variant="rectangular" width={200} height={56} />
                        ) : (
                            <ForecastPeriodDropdown
                                forecastPeriods={selectedForecast ? selectedForecast.forecastPeriods : []}
                                selectedForecastPeriodId={selectedForecastPeriodId}
                                onForecastPeriodSelected={handleForecastPeriodSelected}
                                width={200}
                            />
                        )}
                    </Grid>
                    <Grid item xs={12} sm="auto">
                        {loadingForecasts ? (
                            <Skeleton variant="rectangular" width={200} height={56} />
                        ) : (
                            <FormControl sx={{ minWidth: 200 }}>
                                <InputLabel id="view-select-label">View</InputLabel>
                                <Select 
                                    labelId="view-select-label"
                                    label="View"
                                    onChange={handleChangeView} 
                                    value={view}
                                >
                                    <MenuItem value="units">Units</MenuItem>
                                    <MenuItem value="grossRev">Gross Revenue</MenuItem>
                                    <MenuItem value="netRev">Net Revenue</MenuItem>
                                    <MenuItem value="triggers">Triggers</MenuItem>
                                    <MenuItem value="fixedSpend">Fixed Spend</MenuItem>
                                    <MenuItem value="grossProfit">Gross Profit</MenuItem>
                                    <MenuItem value="grossProfitPercent">Gross Profit Percent</MenuItem>
                                </Select>
                            </FormControl>
                        )}
                    </Grid>
                    <Grid item xs={12} sm="auto">
                        <ProductFiltersButton
                            filtersExpanded={filtersExpanded}
                            onToggle={handleFiltersToggle}
                            disabled={loadingForecasts}
                        />
                    </Grid>
                    {viewSupportsActuals && (
                        <>
                            <Grid item xs={12} sm="auto">
                                <FormControlLabel
                                    control={
                                        <Switch
                                            size="small"
                                            checked={showActuals}
                                            onChange={handleActualsToggle}
                                            disabled={loadingForecasts || loadingTable}
                                            inputProps={{ 'aria-label': 'toggle actuals' }}
                                        />
                                    }
                                    label={
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            Actuals
                                            {loadingActuals && <CircularProgress size={16} />}
                                        </Box>
                                    }
                                    labelPlacement="end"
                                />
                            </Grid>
                            {showActuals && !loadingActuals && (
                                <Grid item xs={12} sm="auto">
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                size="small"
                                                checked={showCompare}
                                                onChange={handleCompareToggle}
                                                disabled={loadingForecasts || loadingTable}
                                                inputProps={{ 'aria-label': 'toggle compare' }}
                                            />
                                        }
                                        label="Compare"
                                        labelPlacement="end"
                                    />
                                </Grid>
                            )}
                        </>
                    )}
                </Grid>
            </Paper>

            <ProductFilters
                selectedForecastId={selectedForecastId}
                selectedForecastPeriodId={selectedForecastPeriodId}
                selectedBrands={selectedBrands}
                setSelectedBrands={setSelectedBrands}
                selectedProductGroups={selectedProductGroups}
                setSelectedProductGroups={setSelectedProductGroups}
                selectedProductIds={selectedProductIds}
                setSelectedProductIds={setSelectedProductIds}
                filtersExpanded={filtersExpanded}
                onToggle={handleFiltersToggle}
                selectedCustomerId={null}
                selectedChannelId={null}
            />

            {selectedForecastId && selectedForecastPeriodId !== null && selectedForecastPeriodId > 0 && (
                <Box>
                    <Paper elevation={1} sx={{ mb: 2 }}>
                        <Tabs 
                            value={aggregationPeriod} 
                            onChange={handleAggregationPeriodChange}
                            sx={{ borderBottom: 1, borderColor: 'divider' }}
                        >
                            <Tab label="Monthly" value="monthly" />
                            <Tab label="Quarterly" value="quarterly" />
                            <Tab label="Weekly" value="weekly" />
                        </Tabs>
                    </Paper>
                    {loadingTable ? (
                        <Skeleton variant="rectangular" width="100%" height={600} />
                    ) : (
                        <OutputsSummaryTable 
                            summaryData={summaryData} 
                            view={view} 
                            startMonth={summaryData?.startMonth || 1} 
                            selectedForecast={selectedForecast}
                            aggregationPeriod={aggregationPeriod}
                            showActuals={showActuals}
                            loadingActuals={loadingActuals}
                            showCompare={showCompare}
                        />
                    )}
                </Box>
            )}
        </Box>
    );
};

export default ForecastOutputsSummary;
